import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  Plus,
  Trash2,
  Repeat,
  BarChart3,
  CheckCircle2,
  XCircle,
} from "lucide-react";

export interface DefectDetailItem {
  defectCode: string;
  defectName: string;
  processStage: string;
  severity: "CRITICAL" | "MAJOR" | "MINOR";
  defectQty: number;
  isRepeated: boolean;
  note?: string;
}

export type DefectTallyItem = DefectDetailItem;

export function normalizeDefectTallyItems(raw: any): DefectDetailItem[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw
    .filter((it: any) => it && typeof it === "object")
    .map((it: any) => {
      const qty = Math.max(0, Math.round(Number(it.defectQty ?? it.qty ?? it.count ?? 0)));
      return {
        defectCode: String(it.defectCode || it.code || "DEF-01"),
        defectName: String(it.defectName || it.name || "Lỗi ngoại quan"),
        processStage: String(it.processStage || it.stage || "PRINTING"),
        severity:
          it.severity === "CRITICAL" || it.severity === "MINOR"
            ? it.severity
            : "MAJOR",
        defectQty: qty,
        isRepeated: Boolean(it.isRepeated) || qty >= 2,
        note: it.note ? String(it.note) : "",
      };
    });
}

export interface StageDefectOption {
  code: string;
  name: string;
  severity: "CRITICAL" | "MAJOR" | "MINOR";
  processStage: string;
}

export const PROCESS_STAGE_LABELS: Record<string, string> = {
  IQC: "IQC - Vật tư đầu vào",
  CUTTING: "1. Cắt / Xả giấy",
  PRINTING: "2. In Offset",
  LAMINATING: "3. Cán màng",
  COATING: "4. Tráng phủ UV / OPV",
  DIE_CUTTING: "5. Bế / Ép kim / Dập nổi",
  GLUING: "6. Dán / Gấp hộp",
  FQC: "FQC - Thành phẩm xuất xưởng",
};

// Nhãn mức lỗi (giá trị lưu giữ nguyên CRITICAL / MAJOR / MINOR)
export const DEFECT_SEVERITY_LABELS: Record<string, string> = {
  CRITICAL: "Nghiêm trọng",
  MAJOR: "Nặng",
  MINOR: "Nhẹ",
};

// Top-level dictionary of standard Offset Packaging defects by stage (Rule 11 Module-Level Export Safety)
export const STAGE_DEFECT_CATALOG: Record<string, StageDefectOption[]> = {
  IQC: [
    { code: "IQC-GSM", name: "Sai lệch định lượng giấy (GSM)", severity: "MAJOR", processStage: "IQC" },
    { code: "IQC-MOIST", name: "Độ ẩm giấy vượt chuẩn (Cong vênh)", severity: "CRITICAL", processStage: "IQC" },
    { code: "IQC-CALIPER", name: "Sai lệch độ dày giấy (Caliper)", severity: "MAJOR", processStage: "IQC" },
    { code: "IQC-GRAIN", name: "Sai hướng sớ giấy (Grain Direction)", severity: "CRITICAL", processStage: "IQC" },
    { code: "IQC-SURF", name: "Bề mặt giấy đốm bẩn / rách biên / ẩm mốc", severity: "MAJOR", processStage: "IQC" },
    { code: "IQC-DYNE", name: "Màng cán không đủ sức căng bề mặt (Dyne < 38)", severity: "CRITICAL", processStage: "IQC" },
    { code: "IQC-INK", name: "Mực in / Hóa chất lệch màu hoặc độ nhớt", severity: "MAJOR", processStage: "IQC" },
  ],
  CUTTING: [
    { code: "CUT-DIM", name: "Lệch kích thước khổ cắt (> ±0.5mm)", severity: "MAJOR", processStage: "CUTTING" },
    { code: "CUT-SKEW", name: "Xéo góc ke vuông / lệch tay kê", severity: "CRITICAL", processStage: "CUTTING" },
    { code: "CUT-BURR", name: "Mép cắt xơ sợi / mẻ dao cắt", severity: "MINOR", processStage: "CUTTING" },
    { code: "CUT-DIRT", name: "Bẩn mép giấy / cong góc khi xả", severity: "MINOR", processStage: "CUTTING" },
  ],
  PRINTING: [
    { code: "PRT-DELTAE", name: "Lệch màu in so với mẫu ký (Delta E > 2.0)", severity: "CRITICAL", processStage: "PRINTING" },
    { code: "PRT-REG", name: "Lệch chồng màu / nhảy tay kê (Misregistration)", severity: "CRITICAL", processStage: "PRINTING" },
    { code: "PRT-SCUM", name: "Bẩn bản / dính mực / chấm mực (Scumming/Hickeys)", severity: "MAJOR", processStage: "PRINTING" },
    { code: "PRT-SETOFF", name: "Dính lưng tờ in (Set-off)", severity: "MAJOR", processStage: "PRINTING" },
    { code: "PRT-GHOST", name: "Bóng ma / sọc lô mực (Ghosting/Streaks)", severity: "MAJOR", processStage: "PRINTING" },
    { code: "PRT-TEXT", name: "Mất nét chữ nhỏ / nhòe mã vạch", severity: "CRITICAL", processStage: "PRINTING" },
    { code: "PRT-PICK", name: "Lột mặt giấy (Paper Picking)", severity: "MINOR", processStage: "PRINTING" },
  ],
  LAMINATING: [
    { code: "LAM-BUBBLE", name: "Nổi bọt khí / rộp màng (Blistering)", severity: "MAJOR", processStage: "LAMINATING" },
    { code: "LAM-WRINKLE", name: "Nhăn màng / sóng màng (Wrinkling)", severity: "MAJOR", processStage: "LAMINATING" },
    { code: "LAM-PEEL", name: "Bong tróc màng / không bám mực (Delamination)", severity: "CRITICAL", processStage: "LAMINATING" },
    { code: "LAM-CURL", name: "Cong tờ in sau cán màng (Curling)", severity: "MINOR", processStage: "LAMINATING" },
  ],
  COATING: [
    { code: "COA-MISS", name: "Lệch vị trí UV định hình (Spot UV Misregister)", severity: "MAJOR", processStage: "COATING" },
    { code: "COA-GLOSS", name: "Không đạt độ bóng / sần vỏ cam (Orange Peel)", severity: "MINOR", processStage: "COATING" },
    { code: "COA-GLUE", name: "Phủ UV tràn vào mép dán keo", severity: "CRITICAL", processStage: "COATING" },
    { code: "COA-CRACK", name: "Nứt màng UV khi gấp (UV Cracking)", severity: "MAJOR", processStage: "COATING" },
  ],
  DIE_CUTTING: [
    { code: "DIE-SHIFT", name: "Lệch vị trí bế so với hình in (> 0.5mm)", severity: "CRITICAL", processStage: "DIE_CUTTING" },
    { code: "DIE-CRACK", name: "Nứt gân cấn / nổ mặt giấy khi gập 180°", severity: "CRITICAL", processStage: "DIE_CUTTING" },
    { code: "DIE-TEAR", name: "Bế không đứt / xơ rách mép hộp", severity: "MAJOR", processStage: "DIE_CUTTING" },
    { code: "DIE-FOIL", name: "Ép kim mất nét / bong nhũ / lệch nhũ", severity: "MAJOR", processStage: "DIE_CUTTING" },
    { code: "DIE-EMBOSS", name: "Dập nổi không sâu / thủng giấy", severity: "MINOR", processStage: "DIE_CUTTING" },
  ],
  GLUING: [
    { code: "GLU-OPEN", name: "Bung mép dán / không rách xơ giấy (Fiber Tear Fail)", severity: "CRITICAL", processStage: "GLUING" },
    { code: "GLU-OOZE", name: "Xì keo / tràn keo dính mặt trong hộp", severity: "CRITICAL", processStage: "GLUING" },
    { code: "GLU-SKEW", name: "Dán lệch đường gấp / Fishtailing", severity: "MAJOR", processStage: "GLUING" },
    { code: "GLU-SCRATCH", name: "Trầy xước bề mặt hộp do băng tải máy dán", severity: "MINOR", processStage: "GLUING" },
    { code: "GLU-COUNT", name: "Đếm thiếu số lượng hoặc lẫn mã hàng", severity: "CRITICAL", processStage: "GLUING" },
  ],
  FQC: [
    { code: "FQC-DIM", name: "Sai kích thước lọt lòng / phủ bì hộp 3D", severity: "CRITICAL", processStage: "FQC" },
    { code: "FQC-BARCODE", name: "Mã vạch không quét được (Grade < B)", severity: "CRITICAL", processStage: "FQC" },
    { code: "FQC-COLOR", name: "Đồng nhất màu sắc lô hàng vượt dung sai", severity: "MAJOR", processStage: "FQC" },
    { code: "FQC-DIRT", name: "Bẩn ngoại quan / trầy xước / móp góc hộp", severity: "MINOR", processStage: "FQC" },
    { code: "FQC-PACK", name: "Đóng thùng sai quy cách / thiếu tem nhãn", severity: "MAJOR", processStage: "FQC" },
  ],
};

export function getDefectsForStage(stageCode?: string | null): StageDefectOption[] {
  const key = (stageCode || "").toUpperCase();
  if (key && STAGE_DEFECT_CATALOG[key]) {
    return STAGE_DEFECT_CATALOG[key];
  }
  return STAGE_DEFECT_CATALOG.PRINTING;
}

export function summarizeDefectDetails(items: DefectDetailItem[] = []) {
  let criticalDefects = 0;
  let majorDefects = 0;
  let minorDefects = 0;
  let totalDefectQty = 0;
  let repeatedDefectTypes = 0;

  for (const item of items) {
    const qty = Math.max(0, Number(item.defectQty) || 0);
    totalDefectQty += qty;
    if (qty >= 2 || item.isRepeated) repeatedDefectTypes += 1;
    if (item.severity === "CRITICAL") criticalDefects += qty;
    else if (item.severity === "MINOR") minorDefects += qty;
    else majorDefects += qty;
  }

  return {
    criticalDefects,
    majorDefects,
    minorDefects,
    totalDefectQty,
    repeatedDefectTypes,
  };
}

interface StageDefectSelectorTableProps {
  processStage?: string | null;
  lotSize?: number;
  sampleSize?: number;
  defectDetails: DefectDetailItem[];
  onChange: (
    nextItems: DefectDetailItem[],
    summary: {
      criticalDefects: number;
      majorDefects: number;
      minorDefects: number;
      totalDefectQty: number;
      okSampleQty: number;
      ngSampleQty: number;
    }
  ) => void;
  readOnly?: boolean;
  /** Ẩn huy hiệu N / n / Mẫu đạt / Mẫu lỗi ở đầu bảng (form đã hiện ở khối Lấy mẫu và Kết luận) */
  hideSampleSummary?: boolean;
}

export const StageDefectSelectorTable: React.FC<StageDefectSelectorTableProps> = ({
  processStage = "PRINTING",
  lotSize = 0,
  sampleSize = 0,
  defectDetails = [],
  onChange,
  readOnly = false,
  hideSampleSummary = false,
}) => {
  const normalizedStage = (processStage || "PRINTING").toUpperCase();
  const [selectedStageTab, setSelectedStageTab] = useState<string>(
    STAGE_DEFECT_CATALOG[normalizedStage] ? normalizedStage : "PRINTING"
  );
  const [customCode, setCustomCode] = useState("");
  const [customName, setCustomName] = useState("");
  const [customSeverity, setCustomSeverity] = useState<"CRITICAL" | "MAJOR" | "MINOR">("MAJOR");

  const safeItems: DefectDetailItem[] = Array.isArray(defectDetails) ? defectDetails : [];

  const { data: sysConfigs } = useQuery({
    queryKey: ["configs", "QC_DEFECT_CATALOG"],
    queryFn: () => trpcClient.config.getAll.query(),
    staleTime: 5 * 60 * 1000,
  });

  const stageOptions = useMemo(() => {
    const base = getDefectsForStage(selectedStageTab);
    const direct = (sysConfigs as any)?.QC_DEFECT_CATALOG;
    if (direct && Array.isArray(direct[selectedStageTab])) {
      return direct[selectedStageTab];
    }
    const cfgList = Array.isArray(sysConfigs) ? sysConfigs : (sysConfigs as any)?.items || [];
    const customCat = cfgList.find((c: any) => c.key === "QC_DEFECT_CATALOG");
    if (customCat?.value) {
      try {
        const parsed = typeof customCat.value === "string" ? JSON.parse(customCat.value) : customCat.value;
        if (parsed && Array.isArray(parsed[selectedStageTab])) {
          return parsed[selectedStageTab];
        }
      } catch {
        // Fallback to standard catalog
      }
    }
    return base;
  }, [selectedStageTab, sysConfigs]);

  const emitChange = (nextItems: DefectDetailItem[]) => {
    const normalized = nextItems.map((it) => ({
      ...it,
      defectQty: Math.max(0, Math.round(Number(it.defectQty) || 0)),
      isRepeated: Math.round(Number(it.defectQty) || 0) >= 2 || Boolean(it.isRepeated),
    }));
    const summary = summarizeDefectDetails(normalized);
    const ngSampleQty = summary.totalDefectQty;
    const okSampleQty = Math.max(0, (Number(sampleSize) || 0) - ngSampleQty);
    onChange(normalized, {
      ...summary,
      okSampleQty,
      ngSampleQty,
    });
  };

  const handleToggleOrIncrementCatalogDefect = (opt: StageDefectOption) => {
    if (readOnly) return;
    const idx = safeItems.findIndex((i) => i.defectCode === opt.code);
    if (idx >= 0) {
      const next = [...safeItems];
      const nextQty = (Number(next[idx].defectQty) || 0) + 1;
      next[idx] = {
        ...next[idx],
        defectQty: nextQty,
        isRepeated: nextQty >= 2,
      };
      emitChange(next);
    } else {
      emitChange([
        ...safeItems,
        {
          defectCode: opt.code,
          defectName: opt.name,
          processStage: opt.processStage,
          severity: opt.severity,
          defectQty: 1,
          isRepeated: false,
          note: "",
        },
      ]);
    }
  };

  const handleAddCustomDefect = () => {
    if (!customName.trim()) return;
    const code =
      customCode.trim().toUpperCase() ||
      `${selectedStageTab}-CUS-${safeItems.length + 1}`;
    emitChange([
      ...safeItems,
      {
        defectCode: code,
        defectName: customName.trim(),
        processStage: selectedStageTab,
        severity: customSeverity,
        defectQty: 1,
        isRepeated: false,
        note: "",
      },
    ]);
    setCustomCode("");
    setCustomName("");
  };

  const handleUpdateRow = (index: number, patch: Partial<DefectDetailItem>) => {
    const next = safeItems.map((item, idx) =>
      idx === index ? { ...item, ...patch } : item
    );
    emitChange(next);
  };

  const handleRemoveRow = (index: number) => {
    emitChange(safeItems.filter((_, idx) => idx !== index));
  };

  const summary = summarizeDefectDetails(safeItems);
  const ngSampleQty = summary.totalDefectQty;
  const okSampleQty = Math.max(0, (Number(sampleSize) || 0) - ngSampleQty);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-2xs space-y-2.5 p-3.5">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-rose-600" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-800">
            Lỗi phát hiện
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {hideSampleSummary ? (
            <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 font-mono">
              Nghiêm trọng {summary.criticalDefects} · Nặng {summary.majorDefects} · Nhẹ {summary.minorDefects}
            </Badge>
          ) : (
          <>
          <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 font-mono">
            N: {Number(lotSize || 0).toLocaleString("vi-VN")}
          </Badge>
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-mono">
            n: {Number(sampleSize || 0).toLocaleString("vi-VN")}
          </Badge>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono font-bold">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Mẫu đạt: {okSampleQty.toLocaleString("vi-VN")}
          </Badge>
          <Badge
            variant="outline"
            className={
              ngSampleQty > 0
                ? "bg-red-50 text-red-700 border-red-300 font-mono font-bold"
                : "bg-slate-50 text-slate-600 border-slate-200 font-mono"
            }
          >
            <XCircle className="w-3 h-3 mr-1" />
            Mẫu lỗi: {ngSampleQty.toLocaleString("vi-VN")}
          </Badge>
          </>
          )}
          {summary.repeatedDefectTypes > 0 && (
            <Badge className="bg-amber-100 text-amber-900 border border-amber-300 font-bold">
              <Repeat className="w-3 h-3 mr-1" />
              {summary.repeatedDefectTypes} lỗi lặp
            </Badge>
          )}
        </div>
      </div>

      {!readOnly && (
        <div className="space-y-2 bg-slate-50/70 rounded-lg p-2.5 border border-slate-200/70">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] font-bold text-slate-700">
              Chọn nhanh mã lỗi theo công đoạn (nhấn lặp để +1):
            </span>
            <Select value={selectedStageTab} onValueChange={setSelectedStageTab}>
              <SelectTrigger className="h-7 w-56 bg-white text-xs font-semibold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PROCESS_STAGE_LABELS).map(([code, label]) => (
                  <SelectItem key={code} value={code} className="text-xs">
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {stageOptions.map((opt) => {
              const found = safeItems.find((i) => i.defectCode === opt.code);
              return (
                <button
                  key={opt.code}
                  type="button"
                  onClick={() => handleToggleOrIncrementCatalogDefect(opt)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-all cursor-pointer ${
                    found
                      ? "bg-rose-600 text-white border-rose-700 shadow-2xs"
                      : "bg-white text-slate-700 border-slate-200 hover:border-rose-300 hover:bg-rose-50/50"
                  }`}
                >
                  <span className="font-mono font-bold text-[10px] opacity-85">
                    {opt.code}
                  </span>
                  <span>{opt.name}</span>
                  {found && (
                    <span className="ml-1 px-1.5 py-0.2 rounded-full bg-white text-rose-700 font-mono font-extrabold text-[10px]">
                      x{found.defectQty}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Add custom defect */}
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 pt-1">
            <div className="sm:col-span-3">
              <Input
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value)}
                placeholder="Mã lỗi (VD: PRT-09)..."
                className="h-8 text-xs font-mono bg-white"
              />
            </div>
            <div className="sm:col-span-5">
              <Input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Nhập tên lỗi phát sinh khác..."
                className="h-8 text-xs bg-white"
              />
            </div>
            <div className="sm:col-span-2">
              <Select
                value={customSeverity}
                onValueChange={(v: any) => setCustomSeverity(v)}
              >
                <SelectTrigger className="h-8 text-xs bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CRITICAL">Nghiêm trọng</SelectItem>
                  <SelectItem value="MAJOR">Nặng</SelectItem>
                  <SelectItem value="MINOR">Nhẹ</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddCustomDefect}
                className="w-full h-8 text-xs font-semibold border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Thêm lỗi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Recorded Defects Table */}
      {safeItems.length === 0 ? (
        <div className="text-center py-4 text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg">
          Chưa ghi nhận lỗi nào trong lô kiểm tra này (100% mẫu kiểm Đạt OK).
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
              <tr>
                <th className="w-10 py-2 px-2.5 text-center font-bold">#</th>
                <th className="w-28 py-2 px-2.5 text-left font-bold">Mã lỗi</th>
                <th className="py-2 px-2.5 text-left font-bold">Tên lỗi</th>
                <th className="w-32 py-2 px-2.5 text-center font-bold">Mức lỗi</th>
                <th className="w-36 py-2 px-2.5 text-center font-bold">Số lượng</th>
                <th className="w-28 py-2 px-2.5 text-center font-bold">Lặp lại</th>
                <th className="w-56 py-2 px-2.5 text-left font-bold">Ghi chú</th>
                {!readOnly && <th className="w-12 py-2 px-2 text-center font-bold">Xóa</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {safeItems.map((item, idx) => (
                <tr key={`${item.defectCode}-${idx}`} className="hover:bg-rose-50/20">
                  <td className="py-2 px-2.5 text-center font-mono text-slate-400">
                    {idx + 1}
                  </td>
                  <td className="py-2 px-2.5 font-mono font-bold text-rose-700">
                    {item.defectCode}
                  </td>
                  <td className="py-2 px-2.5">
                    <div className="font-semibold text-slate-900">{item.defectName}</div>
                    <div className="text-[10px] text-slate-500">
                      {PROCESS_STAGE_LABELS[item.processStage] || item.processStage}
                    </div>
                  </td>
                  <td className="py-2 px-2.5 text-center">
                    {readOnly ? (
                      <Badge
                        variant="outline"
                        className={
                          item.severity === "CRITICAL"
                            ? "bg-red-50 text-red-700 border-red-200 font-bold"
                            : item.severity === "MINOR"
                              ? "bg-slate-100 text-slate-700 border-slate-200"
                              : "bg-amber-50 text-amber-800 border-amber-200 font-semibold"
                        }
                      >
                        {DEFECT_SEVERITY_LABELS[item.severity] || item.severity}
                      </Badge>
                    ) : (
                      <Select
                        value={item.severity || "MAJOR"}
                        onValueChange={(v: any) => handleUpdateRow(idx, { severity: v })}
                      >
                        <SelectTrigger className="h-7 text-[11px] font-bold">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CRITICAL">Nghiêm trọng</SelectItem>
                          <SelectItem value="MAJOR">Nặng</SelectItem>
                          <SelectItem value="MINOR">Nhẹ</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="py-2 px-2.5 text-center">
                    {readOnly ? (
                      <span className="font-mono font-bold text-rose-700">
                        {item.defectQty}
                      </span>
                    ) : (
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            handleUpdateRow(idx, {
                              defectQty: Math.max(1, (Number(item.defectQty) || 1) - 1),
                            })
                          }
                          className="w-6 h-7 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 font-bold"
                        >
                          -
                        </button>
                        <Input
                          type="number"
                          min={1}
                          value={item.defectQty}
                          onChange={(e) =>
                            handleUpdateRow(idx, {
                              defectQty: Math.max(1, Number(e.target.value) || 1),
                            })
                          }
                          className="w-16 h-7 text-center font-mono font-bold text-xs px-1"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleUpdateRow(idx, {
                              defectQty: (Number(item.defectQty) || 0) + 1,
                            })
                          }
                          className="w-6 h-7 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 font-bold"
                        >
                          +
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2.5 text-center">
                    {item.defectQty >= 2 || item.isRepeated ? (
                      <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-bold">
                        <Repeat className="w-3 h-3 mr-1" />
                        Lặp lại ({item.defectQty})
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-slate-500">
                        Đơn lẻ
                      </Badge>
                    )}
                  </td>
                  <td className="py-2 px-2.5">
                    {readOnly ? (
                      <span className="text-slate-600">{item.note || "—"}</span>
                    ) : (
                      <Input
                        value={item.note || ""}
                        onChange={(e) => handleUpdateRow(idx, { note: e.target.value })}
                        placeholder="Vị trí lỗi / nguyên nhân..."
                        className="h-7 text-xs"
                      />
                    )}
                  </td>
                  {!readOnly && (
                    <td className="py-2 px-2 text-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(idx)}
                        className="p-1 text-slate-400 hover:text-red-600 rounded"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
